import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
 Layout,
 Typography,
 Button,
 Space,
 Input,
 Select,
 Tabs,
 Modal,
 message,
 Row,
 Col,
 Spin,
 Switch,
 Card,
 DatePicker,
 Badge,
 Alert
} from 'antd';
import {
 PlusOutlined,
 SearchOutlined,
 ReloadOutlined,
 ExclamationCircleOutlined,
 FilePdfOutlined,
 UserOutlined
} from '@ant-design/icons';

import MapeamentosStats from '../../components/dashboards/Mapeamentos/MapeamentosStats';
import QuestionarioList from '../../components/dashboards/Mapeamentos/QuestionarioList';
import TitularList from '../../components/dashboards/Mapeamentos/TitularList';
import QuestionarioForm from '../../components/dashboards/Mapeamentos/QuestionarioForm';
import TitularForm from '../../components/dashboards/Mapeamentos/TitularForm';
import MapeamentosRelatorioModal from '../../components/dashboards/Mapeamentos/MapeamentosRelatorioModal';
import useMapeamentosRelatorio from '../../hooks/useMapeamentosRelatorio';
import mapeamentosService from '../../services/dashboard/mapeamentos.service';

import {
 Questionario,
 TitularDetalhado,
 CreateQuestionarioDTO,
 CreateTitularDTO,
 Area,
 Dado
} from '../../types/dashboard/mapeamentos.types';
import { GlobalContext } from '../../context/globalContext';
import { GlobalContextType } from '../../@types/global';

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { confirm } = Modal;
const { RangePicker } = DatePicker;

interface Cliente {
 _id: string;
 nome: string;
}

interface ClienteGlobal {
 _id: string;
 nome?: string;
 razao_social?: string;
 inativo?: boolean;
}

const MapeamentosPage: React.FC = () => {
 const { 
   clientes: clientesGlobal, 
   currentCliente 
 } = React.useContext(GlobalContext) as GlobalContextType;

 const relatorioHook = useMapeamentosRelatorio();
 const {
   relatorioModalVisible,
   closeRelatorioModal,
   openRelatorioModal,
   questionarios: questionariosRelatorio,
   loadingQuestionarios: loadingQuestionariosRelatorio
 } = relatorioHook;

 const selectedCliente = currentCliente?._id || '';
 const selectedClienteNome = currentCliente?.nome || currentCliente?.razao_social || '';

 const [activeTab, setActiveTab] = useState<string>('questionarios');
 const [questionarios, setQuestionarios] = useState<Questionario[]>([]);
 const [titulares, setTitulares] = useState<TitularDetalhado[]>([]);
 const [stats, setStats] = useState({
   total_questionarios: 0,
   questionarios_concluidos: 0,
   total_titulares: 0,
   categorias_titulares: 0,
   dados_sensiveis: 0,
   dados_comuns: 0,
   areas_mapeadas: 0,
   total_processos: 0,
   total_perguntas: 0,
   taxa_conclusao: 0
 });

 const [areas, setAreas] = useState<Area[]>([]);
 const [dados, setDados] = useState<Dado[]>([]);
 const [clientes, setClientes] = useState<Cliente[]>([]);

 const [loadingInitial, setLoadingInitial] = useState<boolean>(true);
 const [loadingStats, setLoadingStats] = useState<boolean>(false);
 const [loadingQuestionarios, setLoadingQuestionarios] = useState<boolean>(false);
 const [loadingTitulares, setLoadingTitulares] = useState<boolean>(false);

 const [questionarioModalVisible, setQuestionarioModalVisible] = useState<boolean>(false);
 const [titularModalVisible, setTitularModalVisible] = useState<boolean>(false);
 const [editingQuestionario, setEditingQuestionario] = useState<Questionario | null>(null);
 const [editingTitular, setEditingTitular] = useState<TitularDetalhado | null>(null);
 const [searchTerm, setSearchTerm] = useState<string>('');
 
 const [loadingByCliente, setLoadingByCliente] = useState<boolean>(false);
 const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date | null>(null);
 
 const [showAdvancedStats, setShowAdvancedStats] = useState<boolean>(false);
 const [showAdvancedFilters, setShowAdvancedFilters] = useState<boolean>(false);
 const [advancedFilters, setAdvancedFilters] = useState({
   abordagem: '',
   status: '',
   dataInicio: null as any,
   dataFim: null as any
 });

 useEffect(() => {
   loadInitialData();
 }, []);

 useEffect(() => {
   if (clientesGlobal) {
     loadClientes();
   }
 }, [clientesGlobal]);

 useEffect(() => {
   if (!loadingInitial) {
     recarregarDadosCliente();
   }
 }, [currentCliente]);

 const loadInitialData = async (): Promise<void> => {
   setLoadingInitial(true);
   const messageKey = 'loading-initial';
   
   try {
     message.loading({ content: 'Carregando dados iniciais...', key: messageKey, duration: 0 });

     await Promise.all([
       loadDados().catch(err => {}),
       loadClientes().catch(err => {})
     ]);

     await Promise.all([
       loadAreas().catch(err => {}),
       loadStats().catch(err => {}),
       loadQuestionarios().catch(err => {}),
       loadTitulares().catch(err => {})
     ]);

     message.destroy(messageKey);
     message.success('✅ Dados carregados com sucesso');
   } catch (error) {
     message.destroy(messageKey);
     const errorMessage = error instanceof Error ? error.message : 'Erro ao carregar dados iniciais';
     message.error(`❌ ${errorMessage}`);
   } finally {
     setLoadingInitial(false);
   }
 };

 const recarregarDadosCliente = useCallback(async (): Promise<void> => {
   if (loadingByCliente) return;
   
   setLoadingByCliente(true);
   
   const messageKey = 'reloading-client-data';
   
   try {
     message.loading({ 
       content: selectedCliente ? `Carregando dados de ${selectedClienteNome}...` : 'Carregando todos os dados...', 
       key: messageKey, 
       duration: 0 
     });

     const reloadPromises = [
       loadStats().catch(err => {
         return null;
       }),
       loadQuestionarios().catch(err => {
         return null;
       }),
       loadTitulares().catch(err => {
         return null;
       }),
       loadAreas().catch(err => {
         return null;
       })
     ];

     const results = await Promise.allSettled(reloadPromises);
     
     const failedLoads = results.filter(result => result.status === 'rejected').length;
     
     setUltimaAtualizacao(new Date());
     message.destroy(messageKey);
     
     if (failedLoads === 0) {
       if (selectedCliente) {
         message.success(`✅ Dados atualizados para ${selectedClienteNome}`);
       } else {
         message.success('✅ Todos os dados atualizados com sucesso');
       }
     } else if (failedLoads < results.length) {
       message.warning(`⚠️ Dados parcialmente carregados (${failedLoads} falhas)`);
     } else {
       message.error('❌ Erro ao carregar dados');
     }
     
   } catch (error) {
     message.destroy(messageKey);
     message.error('❌ Erro crítico ao atualizar dados');
   } finally {
     setLoadingByCliente(false);
   }
 }, [selectedCliente, selectedClienteNome, loadingByCliente]);

 const loadStats = useCallback(async (): Promise<void> => {
   if (loadingStats) return;
   
   setLoadingStats(true);
   
   try {
     const clienteParaEstatisticas = selectedCliente && selectedCliente.trim() !== '' ? selectedCliente : undefined;
     const response = await mapeamentosService.obterEstatisticas(clienteParaEstatisticas);
     
     if (response && response.success && response.data) {
       setStats({
         total_questionarios: response.data.total_questionarios || 0,
         questionarios_concluidos: response.data.questionarios_concluidos || 0,
         total_titulares: response.data.total_titulares || 0,
         categorias_titulares: response.data.categorias_titulares || 0,
         dados_sensiveis: response.data.dados_sensiveis || 0,
         dados_comuns: response.data.dados_comuns || 0,
         areas_mapeadas: response.data.areas_mapeadas || 0,
         total_processos: response.data.total_processos || 0,
         total_perguntas: response.data.total_perguntas || 0,
         taxa_conclusao: response.data.taxa_conclusao || 0
       });
     } else {
       setStats({
         total_questionarios: 0,
         questionarios_concluidos: 0,
         total_titulares: 0,
         categorias_titulares: 0,
         dados_sensiveis: 0,
         dados_comuns: 0,
         areas_mapeadas: 0,
         total_processos: 0,
         total_perguntas: 0,
         taxa_conclusao: 0
       });
     }
     
   } catch (error: any) {
     const errorMessage = error instanceof Error ? error.message : 'Erro ao carregar estatísticas';
     
     if (!error?.message?.includes('encontrado')) {
       // Silent warning for non-critical errors
     }
     
     setStats({
       total_questionarios: 0,
       questionarios_concluidos: 0,
       total_titulares: 0,
       categorias_titulares: 0,
       dados_sensiveis: 0,
       dados_comuns: 0,
       areas_mapeadas: 0,
       total_processos: 0,
       total_perguntas: 0,
       taxa_conclusao: 0
     });
   } finally {
     setLoadingStats(false);
   }
 }, [selectedCliente, loadingStats]);

 const loadQuestionarios = useCallback(async (): Promise<void> => {
   if (loadingQuestionarios) return;

   setLoadingQuestionarios(true);
   
   try {
     const filtros: any = {};
     if (selectedCliente && selectedCliente.trim() !== '') {
       filtros.cliente_id = selectedCliente.trim();
     }
     
     const data = await mapeamentosService.listarQuestionarios(filtros);
     
     let questionariosArray: Questionario[] = [];
     if (Array.isArray(data)) {
       questionariosArray = data;
     } else if (data && typeof data === 'object' && 'data' in data && Array.isArray((data as any).data)) {
       questionariosArray = (data as any).data;
     }
     
     setQuestionarios(questionariosArray);
   } catch (error: any) {
     const errorMessage = error instanceof Error ? error.message : 'Erro ao carregar questionários';
     
     if (!error?.message?.includes('encontrado')) {
       message.error(errorMessage);
     }
     
     setQuestionarios([]);
   } finally {
     setLoadingQuestionarios(false);
   }
 }, [selectedCliente, loadingQuestionarios]);

 const loadTitulares = useCallback(async (): Promise<void> => {
   if (loadingTitulares) return;

   setLoadingTitulares(true);
   
   try {
     const filtros: any = {};
     
     if (selectedCliente && selectedCliente.trim() !== '') {
       filtros.cliente_id = selectedCliente.trim();
     }
     
     const data = await mapeamentosService.listarTitulares(filtros);
     
     let titularesArray: TitularDetalhado[] = [];
     if (Array.isArray(data)) {
       titularesArray = data;
     } else if (data && typeof data === 'object' && 'data' in data && Array.isArray((data as any).data)) {
       titularesArray = (data as any).data;
     }
     
     setTitulares(titularesArray);
   } catch (error: any) {
     const errorMessage = error instanceof Error ? error.message : 'Erro ao carregar titulares';
     
     if (!error?.message?.includes('encontrado')) {
       message.error(errorMessage);
     }
     
     setTitulares([]);
   } finally {
     setLoadingTitulares(false);
   }
 }, [selectedCliente, loadingTitulares]);

 const loadAreas = async (): Promise<void> => {
   try {
     const filtros: any = {};
     if (selectedCliente && selectedCliente.trim() !== '') {
       filtros.cliente_id = selectedCliente.trim();
     }
     
     const data = await mapeamentosService.listarAreas(filtros);
     
     let areasArray: Area[] = [];
     if (Array.isArray(data)) {
       areasArray = data;
     } else if (data && typeof data === 'object' && 'data' in data && Array.isArray((data as any).data)) {
       areasArray = (data as any).data;
     }
     
     setAreas(areasArray);
   } catch (error) {
     setAreas([]);
   }
 };

 const loadDados = async (): Promise<void> => {
   try {
     const data = await mapeamentosService.listarDados();
     
     let dadosArray: Dado[] = [];
     if (Array.isArray(data)) {
       dadosArray = data;
     } else if (data && typeof data === 'object' && 'data' in data && Array.isArray((data as any).data)) {
       dadosArray = (data as any).data;
     }
     
     setDados(dadosArray);
   } catch (error) {
     setDados([]);
   }
 };

 const loadClientes = async (): Promise<void> => {
   try {
     if (clientesGlobal && clientesGlobal.length > 0) {
       const clientesFormatados = clientesGlobal
         .filter((cliente: ClienteGlobal) => !cliente.inativo)
         .map((cliente: ClienteGlobal) => ({
           _id: cliente._id,
           nome: cliente.nome || cliente.razao_social || 'Cliente'
         }));
       setClientes(clientesFormatados);
     } else {
       setClientes([]);
     }
   } catch (error) {
     setClientes([]);
   }
 };

 const handleGerarRelatorio = async (): Promise<void> => {
   try {
     if (openRelatorioModal) {
       await openRelatorioModal();
     } else {
       throw new Error('Função openRelatorioModal não disponível');
     }
   } catch (error) {
     const errorMessage = error instanceof Error ? error.message : 'Erro ao abrir relatórios';
     message.error(errorMessage);
   }
 };

 const handleCreateQuestionario = async (data: CreateQuestionarioDTO): Promise<void> => {
   try {
     const clienteParaRefresh = data.cliente_id || selectedCliente;
     
     await mapeamentosService.criarQuestionario(data);
     
     if (clienteParaRefresh) {
       if (clienteParaRefresh === selectedCliente) {
         await Promise.all([loadQuestionarios(), loadStats()]);
       } else if (!selectedCliente) {
         await Promise.all([loadQuestionarios(), loadStats()]);
       }
     } else {
       await Promise.all([loadQuestionarios(), loadStats()]);
     }
     
     setQuestionarioModalVisible(false);
     setEditingQuestionario(null);
     message.success(editingQuestionario ? 'Questionário atualizado com sucesso' : 'Questionário criado com sucesso');
   } catch (error) {
     const errorMessage = error instanceof Error ? error.message : 'Erro ao salvar questionário';
     message.error(errorMessage);
   }
 };

 const handleEditQuestionario = (questionario: Questionario): void => {
   if (selectedCliente && questionario.cliente_id && questionario.cliente_id !== selectedCliente) {
     message.warning('Este questionário pertence a outro cliente');
     return;
   }
   
   setEditingQuestionario(questionario);
   setQuestionarioModalVisible(true);
 };

 const handleDeleteQuestionario = (id: string): void => {
   const questionario = questionarios.find(q => q._id === id);
   
   if (selectedCliente && questionario?.cliente_id && questionario.cliente_id !== selectedCliente) {
     message.warning('Você não pode excluir questionários de outros clientes');
     return;
   }
   
   confirm({
     title: 'Confirmar exclusão',
     icon: <ExclamationCircleOutlined />,
     content: (
       <div>
         <p>Tem certeza que deseja excluir este questionário?</p>
         {questionario && (
           <p><strong>"{questionario.nome}"</strong></p>
         )}
         <p>Esta ação não pode ser desfeita.</p>
       </div>
     ),
     okText: 'Sim, excluir',
     okType: 'danger',
     cancelText: 'Cancelar',
     onOk: async () => {
       try {
         await mapeamentosService.removerQuestionario(id);
         
         if (!questionario?.cliente_id || 
             questionario.cliente_id === selectedCliente || 
             !selectedCliente) {
           await Promise.all([loadQuestionarios(), loadStats()]);
         }
         
         message.success('Questionário excluído com sucesso');
       } catch (error) {
         const errorMessage = error instanceof Error ? error.message : 'Erro ao excluir questionário';
         message.error(errorMessage);
       }
     }
   });
 };

 const handleViewQuestionario = (id: string): void => {
   message.info('Navegação para detalhes em desenvolvimento');
 };

 const handleCreateVersion = async (questionarioId: string): Promise<void> => {
   try {
     await mapeamentosService.criarVersaoRespostas({ questionario_id: questionarioId });
     message.success('Nova versão criada com sucesso');
     await loadQuestionarios();
   } catch (error) {
     const errorMessage = error instanceof Error ? error.message : 'Erro ao criar versão';
     message.error(errorMessage);
   }
 };

 const handleDuplicateQuestionario = async (questionarioId: string, novoNome: string): Promise<void> => {
   try {
     await mapeamentosService.duplicarQuestionario(questionarioId, novoNome);
     await Promise.all([loadQuestionarios(), loadStats()]);
     message.success('Questionário duplicado com sucesso');
   } catch (error) {
     const errorMessage = error instanceof Error ? error.message : 'Erro ao duplicar questionário';
     message.error(errorMessage);
   }
 };

 const handleCreateTitular = async (data: CreateTitularDTO): Promise<void> => {
   try {
     await mapeamentosService.criarTitular(data);
     
     await Promise.all([loadTitulares(), loadStats()]);
     
     setTitularModalVisible(false);
     setEditingTitular(null);
     message.success(editingTitular ? 'Titular atualizado com sucesso' : 'Titular criado com sucesso');
   } catch (error) {
     const errorMessage = error instanceof Error ? error.message : 'Erro ao salvar titular';
     message.error(errorMessage);
   }
 };

 const handleEditTitular = (titular: TitularDetalhado): void => {
   setEditingTitular(titular);
   setTitularModalVisible(true);
 };

 const handleDeleteTitular = (id: string): void => {
   const titular = titulares.find(t => t._id === id);
   
   confirm({
     title: 'Confirmar exclusão',
     icon: <ExclamationCircleOutlined />,
     content: (
       <div>
         <p>Tem certeza que deseja excluir este titular?</p>
         {titular && (
           <p><strong>"{titular.categoria || 'Titular sem categoria'}"</strong></p>
         )}
         <p>Esta ação não pode ser desfeita.</p>
       </div>
     ),
     okText: 'Sim, excluir',
     okType: 'danger',
     cancelText: 'Cancelar',
     onOk: async () => {
       try {
         await mapeamentosService.removerTitular(id);
         
         await Promise.all([loadTitulares(), loadStats()]);
         
         message.success('Titular excluído com sucesso');
       } catch (error) {
         const errorMessage = error instanceof Error ? error.message : 'Erro ao excluir titular';
         message.error(errorMessage);
       }
     }
   });
 };

 const handleRefresh = (): void => {
   if (selectedCliente || loadingInitial) {
     recarregarDadosCliente();
   } else {
     loadInitialData();
   }
 };

 const filteredQuestionarios = useMemo(() => {
   return Array.isArray(questionarios) 
     ? questionarios.filter(q => {
         const matchesSearch = !searchTerm || 
           q.nome.toLowerCase().includes(searchTerm.toLowerCase());
         
         const matchesAbordagem = !advancedFilters.abordagem || 
           q.abordagem === advancedFilters.abordagem;
         
         const matchesStatus = !advancedFilters.status || 
           (advancedFilters.status === 'ativo' ? !q.inativo : q.inativo);
         
         let matchesDate = true;
         if (advancedFilters.dataInicio || advancedFilters.dataFim) {
           const questionarioDate = new Date(q.created_at);
           
           if (advancedFilters.dataInicio) {
             matchesDate = matchesDate && questionarioDate >= advancedFilters.dataInicio.toDate();
           }
           
           if (advancedFilters.dataFim) {
             matchesDate = matchesDate && questionarioDate <= advancedFilters.dataFim.toDate();
           }
         }
         
         return matchesSearch && matchesAbordagem && matchesStatus && matchesDate;
       })
     : [];
 }, [questionarios, searchTerm, advancedFilters]);

 const filteredTitulares = useMemo(() => {
   return Array.isArray(titulares)
     ? titulares.filter(t => {
         const matchesSearch = !searchTerm || 
           (t.categoria || '').toLowerCase().includes(searchTerm.toLowerCase());
         
         return matchesSearch;
       })
     : [];
 }, [titulares, searchTerm]);

 const handleCloseQuestionarioModal = (): void => {
   setQuestionarioModalVisible(false);
   setEditingQuestionario(null);
 };

 const handleCloseTitularModal = (): void => {
   setTitularModalVisible(false);
   setEditingTitular(null);
 };

 const handleOpenNewQuestionario = (): void => {
   setEditingQuestionario(null);
   setQuestionarioModalVisible(true);
 };

 const handleOpenNewTitular = (): void => {
   setEditingTitular(null);
   setTitularModalVisible(true);
 };

 const isInitialLoading = loadingInitial;
 const isContentLoading = loadingQuestionarios || loadingTitulares;

 const EmptyStateIndicator: React.FC = () => {
   if (isInitialLoading || loadingByCliente) return null;
   
   const hasQuestionarios = filteredQuestionarios.length > 0;
   const hasTitulares = filteredTitulares.length > 0;
   
   if (!hasQuestionarios && !hasTitulares && selectedCliente) {
     return (
       <Card 
         style={{ 
           marginBottom: 16, 
           borderLeft: '4px solid #faad14',
           backgroundColor: '#fffbe6' 
         }}
       >
         <Text type="warning">
           ℹ️ Nenhum dado encontrado para o cliente <strong>{selectedClienteNome}</strong>. 
           Verifique se há questionários ou titulares cadastrados para este cliente.
         </Text>
       </Card>
     );
   }
   
   return null;
 };

 return (
   <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
     <Content style={{ padding: '24px' }}>
       <div style={{ marginBottom: '24px' }}>
         <Row justify="space-between" align="top">
           <Col>
             <Title level={2} style={{ margin: 0 }}>
               Mapeamento de Dados
             </Title>
             <Paragraph type="secondary">
               Gerencie questionários e titulares de dados conforme LGPD
               {selectedCliente && (
                 <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
                   {' '} - {selectedClienteNome}
                 </span>
               )}
             </Paragraph>
           </Col>
           <Col>
             <Space wrap>
               <Button
                 type="primary"
                 icon={<ReloadOutlined />}
                 onClick={handleRefresh}
                 loading={isInitialLoading || loadingByCliente}
                 disabled={isContentLoading}
                 title="Atualizar todos os dados"
               >
                 Atualizar
               </Button>
               <Button
                 type="primary"
                 icon={<FilePdfOutlined />}
                 onClick={handleGerarRelatorio}
                 disabled={isInitialLoading}
                 title="Gerar relatório PDF"
               >
                 Gerar Relatório
               </Button>
               <Switch
                 checkedChildren="Avançado"
                 unCheckedChildren="Básico"
                 checked={showAdvancedStats}
                 onChange={setShowAdvancedStats}
                 title="Alternar vista de estatísticas"
               />
             </Space>
           </Col>
         </Row>
       </div>

       {isInitialLoading ? (
         <div style={{ 
           display: 'flex', 
           justifyContent: 'center', 
           alignItems: 'center', 
           minHeight: '400px' 
         }}>
           <Spin size="large" tip="Carregando dados iniciais..." />
         </div>
       ) : (
         <>
           <EmptyStateIndicator />

           <div style={{ marginBottom: '24px' }}>
             <MapeamentosStats 
               stats={stats} 
               loading={loadingStats || loadingByCliente} 
               showAdvanced={showAdvancedStats}
               clienteId={selectedCliente}
               clienteNome={selectedClienteNome}
             />
           </div>

           <div style={{ marginBottom: '24px' }}>
             <Row gutter={[16, 16]}>
               <Col xs={24} sm={12} md={8}>
                 <Input
                   placeholder="Pesquisar..."
                   prefix={<SearchOutlined />}
                   value={searchTerm}
                   onChange={(e) => setSearchTerm(e.target.value)}
                   allowClear
                 />
               </Col>
               <Col xs={24} sm={12} md={8}>
                 <Button 
                   type="primary" 
                   onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                   style={{ width: '100%' }}
                 >
                   {showAdvancedFilters ? 'Ocultar' : 'Filtros Avançados'}
                 </Button>
               </Col>

               {showAdvancedFilters && (
                 <Col xs={24}>
                   <Card 
                     size="small" 
                     title="Filtros Avançados"
                     style={{
                       borderColor: '#5f29cc',
                       borderWidth: '2px'
                     }}
                     headStyle={{
                       backgroundColor: '#5f29cc',
                       color: 'white'
                     }}
                   >
                     <Row gutter={[16, 16]}>
                       <Col xs={24} sm={8}>
                         <Select 
                           placeholder="Abordagem" 
                           allowClear
                           style={{ width: '100%' }}
                           value={advancedFilters.abordagem || undefined}
                           onChange={(value) => setAdvancedFilters(prev => ({ ...prev, abordagem: value || '' }))}
                         >
                           <Option value="detalhada">Detalhada</Option>
                           <Option value="simplificada">Simplificada</Option>
                         </Select>
                       </Col>
                       <Col xs={24} sm={8}>
                         <Select 
                           placeholder="Status" 
                           allowClear
                           style={{ width: '100%' }}
                           value={advancedFilters.status || undefined}
                           onChange={(value) => setAdvancedFilters(prev => ({ ...prev, status: value || '' }))}
                         >
                           <Option value="ativo">Ativo</Option>
                           <Option value="inativo">Inativo</Option>
                         </Select>
                       </Col>
                       <Col xs={24} sm={8}>
                         <RangePicker 
                           placeholder={['Data início', 'Data fim']}
                           style={{ width: '100%' }}
                           onChange={(dates) => setAdvancedFilters(prev => ({ 
                             ...prev, 
                             dataInicio: dates?.[0] || null,
                             dataFim: dates?.[1] || null
                           }))}
                           />
                      </Col>
                    </Row>
                  </Card>
                </Col>
              )}
            </Row>
          </div>

          {loadingByCliente && (
            <div style={{ 
              textAlign: 'center', 
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#e6f7ff',
              borderRadius: '6px',
              border: '1px solid #91d5ff'
            }}>
              <Space>
                <Spin size="small" />
                <Text>Atualizando dados do cliente...</Text>
              </Space>
            </div>
          )}

          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            tabBarExtraContent={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={activeTab === 'questionarios' ? handleOpenNewQuestionario : handleOpenNewTitular}
                disabled={isContentLoading || loadingByCliente}
              >
                {activeTab === 'questionarios' ? 'Novo Questionário' : 'Novo Titular'}
              </Button>
            }
          >
            <TabPane 
              tab={
                <Space>
                  <span>Questionários</span>
                  <Badge 
                    count={filteredQuestionarios.length} 
                    style={{ backgroundColor: '#1890ff' }}
                  />
                </Space>
              } 
              key="questionarios"
            >
              <QuestionarioList
                questionarios={filteredQuestionarios}
                onEdit={handleEditQuestionario}
                onDelete={handleDeleteQuestionario}
                onView={handleViewQuestionario}
                onCreateVersion={handleCreateVersion}
                onDuplicate={handleDuplicateQuestionario}
                loading={loadingQuestionarios || loadingByCliente}
                clienteId={selectedCliente}
                clienteNome={selectedClienteNome}
              />
            </TabPane>
            
            <TabPane 
              tab={
                <Space>
                  <span>Titulares</span>
                  <Badge 
                    count={filteredTitulares.length} 
                    style={{ backgroundColor: '#52c41a' }}
                  />
                </Space>
              } 
              key="titulares"
            >
              <TitularList
                titulares={filteredTitulares}
                onEdit={handleEditTitular}
                onDelete={handleDeleteTitular}
                loading={loadingTitulares || loadingByCliente}
                clienteId={selectedCliente}
                clienteNome={selectedClienteNome}
              />
            </TabPane>
          </Tabs>
        </>
      )}

      <Modal
        title={editingQuestionario ? 'Editar Questionário' : 'Novo Questionário'}
        open={questionarioModalVisible}
        onCancel={handleCloseQuestionarioModal}
        footer={null}
        width={800}
        destroyOnClose
      >
        <QuestionarioForm
          onSubmit={handleCreateQuestionario}
          onCancel={handleCloseQuestionarioModal}
          initialData={{
            ...editingQuestionario,
            cliente_id: editingQuestionario?.cliente_id || 
                       (selectedCliente && !editingQuestionario ? selectedCliente : undefined)
          }}
          clientes={clientes}
          areas={areas}
          loading={false}
        />
      </Modal>

      <Modal
        title={editingTitular ? 'Editar Titular' : 'Novo Titular'}
        open={titularModalVisible}
        onCancel={handleCloseTitularModal}
        footer={null}
        width={1200}
        destroyOnClose
      >
        <TitularForm
          onSubmit={handleCreateTitular}
          onCancel={handleCloseTitularModal}
          initialData={editingTitular || undefined}
          dados={dados}
          areas={areas}
          loading={false}
        />
      </Modal>

      <MapeamentosRelatorioModal
        visible={relatorioModalVisible}
        onCancel={closeRelatorioModal}
        clienteId={selectedCliente}
        clienteNome={selectedClienteNome}
        questionarios={questionariosRelatorio}
        loading={loadingQuestionariosRelatorio}
      />
    </Content>
  </Layout>
);
};

export default MapeamentosPage;